import React, { useEffect, useState, useRef, useMemo } from "react";
import dynamic from "next/dynamic";

import { BsArrowUpRight } from "react-icons/bs";

import useWindowSize from "../../../lib/hooks/useWindowSize";

const OverviewSection = dynamic(
  () => import("./sections/OverviewSection/OverviewSection")
);
const LocationSection = dynamic(
  () => import("./sections/LocationSection/LocationSection")
);
const ItinerarySection = dynamic(
  () => import("./sections/ItinerarySection/ItinerarySection")
);
const InclusionExclusionSection = dynamic(
  () => import("./sections/InclusionExclusionSection/InclusionExclusionSection")
);
const OptionsSection = dynamic(
  () => import("./sections/OptionsSection/OptionsSection")
);
const InformationSection = dynamic(
  () => import("./sections/InformationSection/InformationSection")
);
// const ImpactSection = dynamic(() =>
//   import("./sections/ImpactSection/ImpactSection")
// );
import ImpactSection from "./sections/ImpactSection/ImpactSection";
// const RelatedExperiencesSection = dynamic(() =>
//   import("./sections/RelatedExperiencesSection/RelatedExperiencesSection")
// );
import RelatedExperiencesSection from "./sections/RelatedExperiencesSection/RelatedExperiencesSection";

import Accordion from "components/ui/Accordion/Accordion";
import AccordionItem from "components/ui/Accordion/AccordionItem";
import ScrollNav from "components/ui/nav/ScrollNav/ScrollNav";

import styles from "./ExperienceDetails.module.scss";

const ExperienceDetails = ({
  experience,
  mapImage,
  currency,
  relatedExperiences,
}) => {
  const relatedExperiencesRef = useRef();

  const [activeAccordions, setActiveAccordions] = useState([]);

  const hasOverview = experience?.overview?.description == null ? false : true;
  const hasImpact = experience?.overview?.impact == null ? false : true;
  const hasLocation =
    experience?.location?.lngLat?.lat == null ||
    experience?.location?.description == null ||
    experience?.location?.country == null
      ? false
      : true;
  const hasItinerary =
    !experience?.itinerary?.dates || experience?.itinerary?.schedule == null
      ? false
      : true;
  const hasInclusions =
    experience?.inclusions_exclusions == null ? false : true;
  const hasOptions = experience?.options == null ? false : true;
  const hasMoreInformation = experience?.more_info == null ? false : true;

  const anchorTags = [
    {
      tag: "overview-section",
      text: "Overview",
      active: hasOverview,
    },

    {
      tag: "impact-section",
      text: "Impact",
      active: experience?.overview?.impact,
    },
    { tag: "location-section", text: "Location", active: hasLocation },
    { tag: "itinerary-section", text: "Itinerary", active: hasItinerary },
    {
      tag: "inclusion-exclusion-section",
      text: "Inclusions",
      active: hasInclusions,
    },
    { tag: "options-section", text: "Options", active: hasOptions },
    {
      tag: "additional-information-section",
      text: "More Info",
      active: hasMoreInformation,
    },
    {
      tag: "related-experiences-section",
      text: "Related",
      active: !!relatedExperiences.length,
    },
  ];

  const windowSize = useWindowSize();

  useEffect(() => {
    if (windowSize.width > 992) {
      setActiveAccordions(anchorTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setActiveAccordions, windowSize.width]);

  const handleAccordionItemToggle = (id) => {
    setActiveAccordions((previous) => {
      if (previous.find((item) => item.tag === id)) {
        const next = previous.filter((accordion) => accordion.tag !== id);

        return [...next];
      }
      const tag = anchorTags.find((tag) => tag.tag === id);
      return [...previous, tag];
    });
  };

  const accordionItemProps = useMemo(() => {
    return {
      isPageSection: true,
      // startOpen: windowSize.width > 992 ? true : false,
      startOpen: true,
      classNames: ["scroll-margin-top-150"],
      onToggle: handleAccordionItemToggle,
      // titleFontSize: "28px",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSize.width]);

  const handleScrollNavClick = (event, tag) => {
    if (!!activeAccordions.find((accordion) => accordion.tag === tag.tag)) {
      return;
    }
    handleAccordionItemToggle(tag.tag);
  };

  // const { categories } = experience;
  // const categoryList = categories.map((category) => category.category_name);

  return (
    <div className={`${styles.detailsContainer}`} id="mainContent">
      <ScrollNav
        anchorTags={anchorTags}
        style="miniButtons"
        onClick={handleScrollNavClick}
      />
      <Accordion>
        {hasOverview && (
          <AccordionItem
            title="Overview"
            icon="/assets/img/icons/book.svg"
            id="overview-section"
            isOpen={
              !!activeAccordions.find(
                (accordion) => accordion.tag === "overview-section"
              )
            }
            {...accordionItemProps}
          >
            <OverviewSection experience={experience} mapImage={mapImage} />
          </AccordionItem>
        )}
        <AccordionItem
          title={`Impact`}
          icon={<BsArrowUpRight size={25} />}
          id="impact-section"
          isOpen={
            !!activeAccordions.find(
              (accordion) => accordion.tag === "impact-section"
            )
          }
          {...accordionItemProps}
        >
          <ImpactSection />
        </AccordionItem>
        {hasLocation && (
          <AccordionItem
            title={`Location`}
            icon="/assets/img/icons/map-pin-black.svg"
            id="location-section"
            isOpen={
              !!activeAccordions.find(
                (accordion) => accordion.tag === "location-section"
              )
            }
            {...accordionItemProps}
          >
            <LocationSection experience={experience} mapImage={mapImage} />
          </AccordionItem>
        )}
        {hasItinerary && (
          <AccordionItem
            title="Itinerary"
            icon="/assets/img/icons/calendar-2.svg"
            id="itinerary-section"
            isOpen={
              !!activeAccordions.find(
                (accordion) => accordion.tag === "itinerary-section"
              )
            }
            {...accordionItemProps}
          >
            <ItinerarySection experience={experience} />
          </AccordionItem>
        )}
        {hasInclusions && (
          <AccordionItem
            title="Inclusions"
            icon="/assets/img/icons/checklist.svg"
            id="inclusion-exclusion-section"
            isOpen={
              !!activeAccordions.find(
                (accordion) => accordion.tag === "inclusion-exclusion-section"
              )
            }
            {...accordionItemProps}
          >
            <InclusionExclusionSection experience={experience} />
          </AccordionItem>
        )}
        {hasOptions && (
          <AccordionItem
            title="Options"
            icon="/assets/img/icons/options.svg"
            id="options-section"
            isOpen={
              !!activeAccordions.find(
                (accordion) => accordion.tag === "options-section"
              )
            }
            {...accordionItemProps}
          >
            <OptionsSection experience={experience} currency={currency} />
          </AccordionItem>
        )}
        {hasMoreInformation && (
          <AccordionItem
            title="More Information"
            icon="/assets/img/icons/plus-clipboard.svg"
            id="additional-information-section"
            isOpen={
              !!activeAccordions.find(
                (accordion) =>
                  accordion.tag === "additional-information-section"
              )
            }
            {...accordionItemProps}
          >
            <InformationSection experience={experience} />
          </AccordionItem>
        )}
        {!!relatedExperiences.length && (
          <AccordionItem
            title="Related Experiences"
            icon="/assets/img/icons/roadmap.svg"
            id="related-experiences-section"
            scrollElementRef={relatedExperiencesRef}
            scrollButtons={false}
            isOpen={
              !!activeAccordions.find(
                (accordion) => accordion.tag === "related-experiences-section"
              )
            }
            {...accordionItemProps}
          >
            <div ref={relatedExperiencesRef}>
              <RelatedExperiencesSection
                experiences={relatedExperiences}
                sideScroll="all"
                sideScrollButtons={true}
                scrollAfterCardsCount={2}
                cardRowRef={relatedExperiencesRef}
                scrollIndicator={true}
              />
            </div>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
};

export default ExperienceDetails;
